import type MainProject from '@/model/MainProject'
import type FileName from '@/model/ComControl/FileName'
import AvgButton from '@/model/ComControl/AvgButton'
import type DataFileMap from '@/model/Resource/DataFileMap'
import { appendMaterialResourceMappingEntry } from '@/features/material-library/resourceMapping'
import { parseOuiFile, type ParsedOuiProject, type ParsedOuiResource } from '@/features/oui/parseOuiFile'
import type { ResourceCategoryKey } from '@/features/virtual-resource/constants'

type VirtualRoots = Record<ResourceCategoryKey, DataFileMap | null>

export type SkinProgressCallback = (progress: number, status: string) => void

export type SkinResourceEntry = {
  ouiPath: string
  fileName: string
  file: File
  category: ResourceCategoryKey
  folderSegments: string[]
  sourceUrl?: string
}

export type SkinPackage = {
  uiName: string
  bitmap: Uint8Array
  width: number
  height: number
  entries: SkinResourceEntry[]
  parsedProject: ParsedOuiProject
}

export type SkinApplyResult = {
  roots: VirtualRoots
}

type ReferencedSkinFile = {
  ouiPath: string
  category: ResourceCategoryKey
  folderSegments: string[]
  fileName: string
}

const SKIN_RESOURCE_LINK_INDEX = 6
const SKIN_RESOURCE_MARKER = 'oui-skin'

const OUI_FOLDER_MAPPING: Record<string, { category: ResourceCategoryKey; folderSegments: string[] }> = {
  background: { category: 'graphic', folderSegments: ['背景'] },
  button: { category: 'graphic', folderSegments: ['按钮'] },
  chat: { category: 'graphic', folderSegments: ['气泡'] },
  face: { category: 'graphic', folderSegments: ['头像'] },
  half: { category: 'graphic', folderSegments: ['立绘'] },
  oafs: { category: 'graphic', folderSegments: ['动图'] },
  other: { category: 'graphic', folderSegments: ['其他'] },
  system: { category: 'graphic', folderSegments: ['系统'] },
  ui: { category: 'graphic', folderSegments: ['UI'] },
  video: { category: 'video', folderSegments: ['视频'] },
}

function reportProgress(
  callback: SkinProgressCallback | undefined,
  progress: number,
  status: string,
) {
  callback?.(Math.max(0, Math.min(100, Math.round(progress))), status)
}

function guessMimeType(fileName: string): string {
  const lower = fileName.toLowerCase()
  if (lower.endsWith('.png')) return 'image/png'
  if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg'
  if (lower.endsWith('.gif')) return 'image/gif'
  if (lower.endsWith('.webp')) return 'image/webp'
  if (lower.endsWith('.bmp')) return 'image/bmp'
  if (lower.endsWith('.svg')) return 'image/svg+xml'
  if (lower.endsWith('.mp3')) return 'audio/mpeg'
  if (lower.endsWith('.wav')) return 'audio/wav'
  if (lower.endsWith('.ogg')) return 'audio/ogg'
  if (lower.endsWith('.aac')) return 'audio/aac'
  if (lower.endsWith('.m4a')) return 'audio/mp4'
  if (lower.endsWith('.flac')) return 'audio/flac'
  if (lower.endsWith('.mp4')) return 'video/mp4'
  if (lower.endsWith('.webm')) return 'video/webm'
  return 'application/octet-stream'
}

export function trimSkinPath(value: string): string {
  return String(value || '')
    .replace(/\0/g, '')
    .replace(/\\/g, '/')
    .replace(/^\/+|\/+$/g, '')
    .trim()
}

function splitPathSegments(value: string): string[] {
  return trimSkinPath(value)
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean)
}

export function trimSkinPathName(name: string): string {
  const normalized = trimSkinPath(name)
  return normalized.split('/').pop()?.trim() || ''
}

function sanitizeFileName(name: string): string {
  return String(name || '')
    .trim()
    .replace(/[\\/:*?"<>|]/g, '_')
}

export function buildSkinPathAliases(path: string, options?: { includeFileName?: boolean }): string[] {
  const normalized = trimSkinPath(path)
  const segments = splitPathSegments(normalized)
  const aliases = [
    normalized,
    `Graphics/${normalized}`,
    segments.slice(1).join('/'),
  ]
  if (options?.includeFileName !== false) {
    aliases.push(segments[segments.length - 1] || '')
  }
  return aliases
    .map((item) => trimSkinPath(item).toLowerCase())
    .filter(Boolean)
}

export function buildSkinPathAliasSet(paths: Iterable<string>, options?: { includeFileName?: boolean }): Set<string> {
  const aliasSet = new Set<string>()
  for (const path of paths) {
    for (const alias of buildSkinPathAliases(path, options)) {
      aliasSet.add(alias)
    }
  }
  return aliasSet
}

export function hasSkinPathAlias(path: string, aliasSet: Set<string>, options?: { includeFileName?: boolean }): boolean {
  return buildSkinPathAliases(path, options).some((alias) => aliasSet.has(alias))
}

export function summarizeSkinTalkSettings(project: { systemData: MainProject['systemData'] }) {
  return {
    talk: {
      faceStyle: project.systemData.Talk.FaceStyle,
      selectButton: project.systemData.Talk.SelectButton,
      selectPic: project.systemData.Talk.SelectPic,
    },
    talkWin: {
      x: project.systemData.Talk.TalkWin.X,
      y: project.systemData.Talk.TalkWin.Y,
      backPic: project.systemData.Talk.TalkWin.BackPic.name,
      facePic: project.systemData.Talk.TalkWin.FacePic.name,
      faceX: project.systemData.Talk.TalkWin.f_x,
      faceY: project.systemData.Talk.TalkWin.f_y,
      textX: project.systemData.Talk.TalkWin.p_x1,
      textY: project.systemData.Talk.TalkWin.p_y1,
      viewW: project.systemData.Talk.TalkWin.p_viewW,
      viewH: project.systemData.Talk.TalkWin.p_viewH,
    },
    nameWin: {
      textX: project.systemData.Talk.NameWinS.P_x,
      textY: project.systemData.Talk.NameWinS.P_y,
      backPic: project.systemData.Talk.NameWinS.BackPic.name,
      backX: project.systemData.Talk.NameWinS.x,
      backY: project.systemData.Talk.NameWinS.y,
      isCenter: project.systemData.Talk.NameWinS.IsCenter,
    },
  }
}

export function mapOuiPathToVirtualTarget(ouiPath: string): {
  category: ResourceCategoryKey
  folderSegments: string[]
  fileName: string
} | null {
  const segments = splitPathSegments(ouiPath)
  if (!segments.length) return null
  const fileName = sanitizeFileName(segments[segments.length - 1] || '')
  if (!fileName) return null

  const firstSegment = (segments[0] || '').toLowerCase()
  const mapped = OUI_FOLDER_MAPPING[firstSegment] ?? { category: 'graphic' as const, folderSegments: ['其他'] }
  return {
    category: mapped.category,
    folderSegments: [...mapped.folderSegments, ...segments.slice(1, -1)],
    fileName,
  }
}

export function buildSkinResourceEntries(items: ParsedOuiResource[]): SkinResourceEntry[] {
  return items
    .map((item) => {
      const target = mapOuiPathToVirtualTarget(item.name)
      if (!target || !item.content.length) return null
      const buffer = item.content.buffer.slice(
        item.content.byteOffset,
        item.content.byteOffset + item.content.byteLength,
      ) as ArrayBuffer
      return {
        ouiPath: item.name,
        fileName: target.fileName,
        file: new File([buffer], target.fileName, {
          type: guessMimeType(target.fileName),
        }),
        category: target.category,
        folderSegments: target.folderSegments,
      } satisfies SkinResourceEntry
    })
    .filter((item): item is SkinResourceEntry => item != null)
}

export function parseSkinOuiPackage(params: {
  project: MainProject
  source: ArrayBuffer | Uint8Array
  dataVersion: number
  isNew?: boolean
  onProgress?: SkinProgressCallback
}): SkinPackage {
  reportProgress(params.onProgress, 16, '解析 .oui 包头…')
  const parsedOui = parseOuiFile(params.source, params.dataVersion, params.project, params.isNew ?? false)
  const { project: parsedProject } = parsedOui
  reportProgress(params.onProgress, 28, '提取皮肤资源清单…')
  const referencedFiles = collectReferencedSkinFilesFromProject(parsedProject)
  const referencedAliasSet = buildSkinPathAliasSet(Array.from(referencedFiles.values(), (item) => item.ouiPath))
  const allEntries = buildSkinResourceEntries(parsedOui.resources)
  const entries =
    referencedAliasSet.size > 0
      ? allEntries.filter((item) => hasSkinPathAlias(item.ouiPath, referencedAliasSet))
      : allEntries

  return {
    uiName: parsedProject.UiName,
    bitmap: parsedProject.Bitmap,
    width: parsedProject.Width,
    height: parsedProject.Height,
    entries,
    parsedProject,
  }
}

function getPathDepth(path: string): number {
  return splitPathSegments(path).length
}

function setPreferredPathAlias(nameMap: Map<string, string>, alias: string, targetRelativePath: string) {
  const existing = nameMap.get(alias)
  if (!existing || getPathDepth(targetRelativePath) > getPathDepth(existing)) {
    nameMap.set(alias, targetRelativePath)
  }
}

function registerPathAliases(nameMap: Map<string, string>, ouiPath: string, targetRelativePath: string) {
  for (const alias of buildSkinPathAliases(ouiPath)) {
    setPreferredPathAlias(nameMap, alias, targetRelativePath)
  }
}

function replaceMappedPath(raw: string, nameMap: Map<string, string>): string {
  const normalized = trimSkinPath(raw).toLowerCase()
  if (!normalized) return raw
  return nameMap.get(normalized) ?? raw
}

export function rewriteProjectSkinPaths(
  value: unknown,
  nameMap: Map<string, string>,
  seen = new WeakSet<object>(),
): void {
  if (!value || typeof value !== 'object') return
  if (seen.has(value as object)) return
  seen.add(value as object)

  const maybeFileName = value as FileName
  if (typeof maybeFileName.name === 'string' && typeof maybeFileName.type === 'number') {
    maybeFileName.name = replaceMappedPath(maybeFileName.name, nameMap)
    return
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      rewriteProjectSkinPaths(item, nameMap, seen)
    }
    return
  }

  for (const [key, current] of Object.entries(value as Record<string, unknown>)) {
    if (typeof current === 'string' && /Path|Image|Pic|Logo|File|name/i.test(key)) {
      ;(value as Record<string, unknown>)[key] = replaceMappedPath(current, nameMap)
      continue
    }
    rewriteProjectSkinPaths(current, nameMap, seen)
  }
}

function collectReferencedSkinFiles(
  value: unknown,
  out: Map<string, ReferencedSkinFile>,
  seen = new WeakSet<object>(),
): void {
  if (!value || typeof value !== 'object') return
  if (seen.has(value as object)) return
  seen.add(value as object)

  const maybeFileName = value as FileName
  if (typeof maybeFileName.name === 'string' && typeof maybeFileName.type === 'number') {
    const ouiPath = trimSkinPath(maybeFileName.name)
    if (!ouiPath) return
    const target = mapOuiPathToVirtualTarget(ouiPath)
    if (!target) return
    out.set(ouiPath.toLowerCase(), {
      ouiPath,
      category: target.category,
      folderSegments: target.folderSegments,
      fileName: target.fileName,
    })
    return
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      collectReferencedSkinFiles(item, out, seen)
    }
    return
  }

  for (const current of Object.values(value as Record<string, unknown>)) {
    collectReferencedSkinFiles(current, out, seen)
  }
}

export function collectReferencedSkinFilesFromProject(project: {
  systemData: MainProject['systemData']
  GameMallNew?: MainProject['GameMallNew'] | null
  GameMallLimite?: MainProject['GameMallLimite'] | null
  Welfare?: MainProject['Welfare'] | null
}): Map<string, ReferencedSkinFile> {
  const referencedFiles = new Map<string, ReferencedSkinFile>()
  collectReferencedSkinFiles(project.systemData, referencedFiles)
  if (project.GameMallNew) collectReferencedSkinFiles(project.GameMallNew, referencedFiles)
  if (project.GameMallLimite) collectReferencedSkinFiles(project.GameMallLimite, referencedFiles)
  if (project.Welfare) collectReferencedSkinFiles(project.Welfare, referencedFiles)
  return referencedFiles
}

/**
 * 判断资源节点是否为皮肤资源，并兼容旧数据中 `oui-skin` 被压缩到其他下标的情况。
 * @param node 虚拟资源文件节点
 * @returns 是否带有皮肤资源标识
 */
export function hasSkinResourceMarker(node: DataFileMap): boolean {
  return node.links[SKIN_RESOURCE_LINK_INDEX] === SKIN_RESOURCE_MARKER
    || node.links.includes(SKIN_RESOURCE_MARKER)
}

function removeTaggedSkinResources(root: DataFileMap | null): DataFileMap | null {
  if (!root) return root

  function walk(node: DataFileMap, depth: number): boolean {
    if (!node.isFolder) {
      return !hasSkinResourceMarker(node)
    }
    node.children = node.children.filter((child) => walk(child, depth + 1))
    return depth <= 1 || node.children.length > 0
  }

  const cloned = new (root.constructor as typeof DataFileMap)(root.toJson())
  walk(cloned, 0)
  return cloned
}

function clearSkinResourcesFromRoots(roots: VirtualRoots): VirtualRoots {
  return {
    graphic: removeTaggedSkinResources(roots.graphic),
    audio: removeTaggedSkinResources(roots.audio),
    font: removeTaggedSkinResources(roots.font),
    video: removeTaggedSkinResources(roots.video),
  }
}

function markSkinResources(root: DataFileMap | null): DataFileMap | null {
  if (!root) return root
  const cloned = new (root.constructor as typeof DataFileMap)(root.toJson())

  function walk(node: DataFileMap): void {
    if (!node.isFolder) {
      node.links[SKIN_RESOURCE_LINK_INDEX] = SKIN_RESOURCE_MARKER
      return
    }
    for (const child of node.children) walk(child)
  }

  walk(cloned)
  return cloned
}

/**
 * 给 UI/OUIQ 模板资源树补齐皮肤资源标识，确保下次切换皮肤时能被统一清理。
 * @param roots 模板解析得到的虚拟资源根
 * @returns 已克隆并补齐 `oui-skin` 标识的虚拟资源根
 */
function markSkinResourcesInRoots(roots: VirtualRoots): VirtualRoots {
  return {
    graphic: markSkinResources(roots.graphic),
    audio: markSkinResources(roots.audio),
    font: markSkinResources(roots.font),
    video: markSkinResources(roots.video),
  }
}

function cloneSkinResourceRoot(root: DataFileMap | null): DataFileMap | null {
  return root ? new (root.constructor as typeof DataFileMap)(root.toJson()) : null
}

function mergeSkinResourceRoot(baseRoot: DataFileMap | null, incomingRoot: DataFileMap | null): DataFileMap | null {
  if (!baseRoot) return cloneSkinResourceRoot(incomingRoot)
  if (!incomingRoot) return cloneSkinResourceRoot(baseRoot)

  const merged = cloneSkinResourceRoot(baseRoot)!
  function mergeFolder(target: DataFileMap, source: DataFileMap): void {
    const nextChildren = [...target.children]
    for (const sourceChild of source.children) {
      const targetIndex = nextChildren.findIndex((child) =>
        child.name === sourceChild.name && child.isFolder === sourceChild.isFolder,
      )
      if (targetIndex < 0) {
        nextChildren.push(cloneSkinResourceRoot(sourceChild)!)
        continue
      }
      const targetChild = nextChildren[targetIndex]!
      if (targetChild.isFolder && sourceChild.isFolder) {
        mergeFolder(targetChild, sourceChild)
        continue
      }
      nextChildren.splice(targetIndex, 1, cloneSkinResourceRoot(sourceChild)!)
    }
    target.children = nextChildren
  }

  mergeFolder(merged, incomingRoot)
  return merged
}

/**
 * 替换皮肤资源树：先清理旧皮肤资源，再合并新皮肤资源，并确保新资源带 `oui-skin` 标识。
 * @param roots 当前资源管理器虚拟资源根
 * @param incomingRoots 新皮肤解析得到的虚拟资源根
 * @returns 已完成旧皮肤清理和新皮肤写入的资源根
 */
export function replaceSkinResourceRoots(params: {
  roots: VirtualRoots
  incomingRoots: VirtualRoots
}): VirtualRoots {
  const markedIncomingRoots = markSkinResourcesInRoots(params.incomingRoots)
  const cleanedRoots = {
    graphic: removeTaggedSkinResources(params.roots.graphic),
    audio: removeTaggedSkinResources(params.roots.audio),
    font: removeTaggedSkinResources(params.roots.font),
    video: removeTaggedSkinResources(params.roots.video),
  }
  return {
    graphic: mergeSkinResourceRoot(cleanedRoots.graphic, markedIncomingRoots.graphic),
    audio: mergeSkinResourceRoot(cleanedRoots.audio, markedIncomingRoots.audio),
    font: mergeSkinResourceRoot(cleanedRoots.font, markedIncomingRoots.font),
    video: mergeSkinResourceRoot(cleanedRoots.video, markedIncomingRoots.video),
  }
}

function buildButtonFromButtonList(
  buttonList: AvgButton[],
  index: number | null | undefined,
  x?: number,
  y?: number,
): AvgButton {
  const source =
    Number.isInteger(index) && Number(index) >= 0 && Number(index) < buttonList.length
      ? buttonList[Number(index)] ?? null
      : null
  const next = Object.assign(new AvgButton(), source ?? {})
  if (typeof x === 'number') next.X = x
  if (typeof y === 'number') next.Y = y
  return next
}

function rebuildSkinProjectButtons(project: MainProject): void {
  const buttonList = Array.isArray(project.systemData.btnList.buttons) ? project.systemData.btnList.buttons : []

  project.systemData.Title.Buttons = project.systemData.Title.ButtonsEx.map((item) =>
    buildButtonFromButtonList(buttonList, item?.Index, item?.X, item?.Y),
  )
  project.systemData.Title.BIndex = project.systemData.Title.ButtonsEx.map((item) => Number(item?.Index ?? 0))

  project.systemData.GameMenu.Buttons = project.systemData.GameMenu.ButtonsEx.map((item) =>
    buildButtonFromButtonList(buttonList, item?.Index, item?.X, item?.Y),
  )
  project.systemData.GameMenu.BIndex = project.systemData.GameMenu.ButtonsEx.map((item) => Number(item?.Index ?? 0))

  project.systemData.Talk.TalkWin.Buttons = project.systemData.Talk.TalkWin.ButtonsEx.map((item) =>
    buildButtonFromButtonList(buttonList, item?.Index, item?.X, item?.Y),
  )
  project.systemData.Talk.TalkWin.BIndex = project.systemData.Talk.TalkWin.ButtonsEx.map((item) =>
    Number(item?.Index ?? 0),
  )

  project.systemData.Sldata.BackButton = buildButtonFromButtonList(
    buttonList,
    project.systemData.Sldata.BackButtonEx?.Index,
    project.systemData.Sldata.BackButtonEx?.X,
    project.systemData.Sldata.BackButtonEx?.Y,
  )
  project.systemData.Sldata.CloseButton = buildButtonFromButtonList(
    buttonList,
    project.systemData.Sldata.CloseButtonEx?.Index,
    project.systemData.Sldata.CloseButtonEx?.X,
    project.systemData.Sldata.CloseButtonEx?.Y,
  )
  project.systemData.Sldata.BtnIndex = [
    Number(project.systemData.Sldata.BackButtonEx?.Index ?? 0),
    Number(project.systemData.Sldata.CloseButtonEx?.Index ?? 0),
  ]

  project.systemData.CG.BackButton = buildButtonFromButtonList(
    buttonList,
    project.systemData.CG.BackButtonEx?.Index,
    project.systemData.CG.BackButtonEx?.X,
    project.systemData.CG.BackButtonEx?.Y,
  )
  project.systemData.CG.CloseButton = buildButtonFromButtonList(
    buttonList,
    project.systemData.CG.CloseButtonEx?.Index,
    project.systemData.CG.CloseButtonEx?.X,
    project.systemData.CG.CloseButtonEx?.Y,
  )
  project.systemData.CG.BtnIndex = [
    Number(project.systemData.CG.BackButtonEx?.Index ?? 0),
    Number(project.systemData.CG.CloseButtonEx?.Index ?? 0),
  ]

  project.systemData.BGM.Button = buildButtonFromButtonList(
    buttonList,
    project.systemData.BGM.ButtonEx?.Index,
    project.systemData.BGM.ButtonEx?.X,
    project.systemData.BGM.ButtonEx?.Y,
  )
  project.systemData.BGM.CloseButton = buildButtonFromButtonList(
    buttonList,
    project.systemData.BGM.CloseButtonEx?.Index,
    project.systemData.BGM.CloseButtonEx?.X,
    project.systemData.BGM.CloseButtonEx?.Y,
  )
  project.systemData.BGM.BtnIndex = [
    Number(project.systemData.BGM.ButtonEx?.Index ?? 0),
    Number(project.systemData.BGM.CloseButtonEx?.Index ?? 0),
  ]

  project.systemData.backPlay.Close = buildButtonFromButtonList(
    buttonList,
    project.systemData.backPlay.CloseEx?.Index ?? project.systemData.backPlay.ButtonIndex,
    project.systemData.backPlay.CloseEx?.X,
    project.systemData.backPlay.CloseEx?.Y,
  )
  project.systemData.backPlay.ButtonIndex = Number(project.systemData.backPlay.CloseEx?.Index ?? 0)

  project.systemData.SystemMenu.Close = buildButtonFromButtonList(
    buttonList,
    project.systemData.SystemMenu.CloseEx?.Index,
    project.systemData.SystemMenu.CloseEx?.X,
    project.systemData.SystemMenu.CloseEx?.Y,
  )
  project.systemData.SystemMenu.Title = buildButtonFromButtonList(
    buttonList,
    project.systemData.SystemMenu.TitleEx?.Index,
    project.systemData.SystemMenu.TitleEx?.X,
    project.systemData.SystemMenu.TitleEx?.Y,
  )
  project.systemData.SystemMenu.AutoText_on = buildButtonFromButtonList(
    buttonList,
    project.systemData.SystemMenu.AutoText_onEx?.Index,
    project.systemData.SystemMenu.AutoText_onEx?.X,
    project.systemData.SystemMenu.AutoText_onEx?.Y,
  )
  project.systemData.SystemMenu.AutoText_off = buildButtonFromButtonList(
    buttonList,
    project.systemData.SystemMenu.AutoText_offEx?.Index,
    project.systemData.SystemMenu.AutoText_offEx?.X,
    project.systemData.SystemMenu.AutoText_offEx?.Y,
  )
  project.systemData.SystemMenu.FullScreen = buildButtonFromButtonList(
    buttonList,
    project.systemData.SystemMenu.FullScreenEx?.Index,
    project.systemData.SystemMenu.FullScreenEx?.X,
    project.systemData.SystemMenu.FullScreenEx?.Y,
  )
  project.systemData.SystemMenu.WinScreen = buildButtonFromButtonList(
    buttonList,
    project.systemData.SystemMenu.WinScreenEx?.Index,
    project.systemData.SystemMenu.WinScreenEx?.X,
    project.systemData.SystemMenu.WinScreenEx?.Y,
  )
  project.systemData.SystemMenu.ButtonsIndex = [
    Number(project.systemData.SystemMenu.CloseEx?.Index ?? 0),
    Number(project.systemData.SystemMenu.TitleEx?.Index ?? 0),
    Number(project.systemData.SystemMenu.AutoText_onEx?.Index ?? 0),
    Number(project.systemData.SystemMenu.AutoText_offEx?.Index ?? 0),
    Number(project.systemData.SystemMenu.FullScreenEx?.Index ?? 0),
    Number(project.systemData.SystemMenu.WinScreenEx?.Index ?? 0),
  ]
}

export function applySkinPackageToProject(project: MainProject, skinPackage: SkinPackage): void {
  project.UiName = skinPackage.uiName
  project.Bitmap = skinPackage.bitmap
  project.Width = skinPackage.width
  project.Height = skinPackage.height
  project.systemData = skinPackage.parsedProject.systemData
  project.GameMallNew = skinPackage.parsedProject.GameMallNew
  project.GameMallLimite = skinPackage.parsedProject.GameMallLimite
  project.Welfare = skinPackage.parsedProject.Welfare
  project.FilesBuf = []
  rebuildSkinProjectButtons(project)
}

export async function applySkinResources(params: {
  project: MainProject
  roots: VirtualRoots
  entries: SkinResourceEntry[]
  /** 仅调试/兼容旧流程时开启：把未匹配到内嵌资源的引用写成空占位资源。默认关闭，避免旧工具引用路径污染资源树。 */
  includeReferencedPlaceholders?: boolean
  rewriteProjectPaths?: boolean
  onProgress?: SkinProgressCallback
  resolveResource: (
    entry: SkinResourceEntry,
    index: number,
    total: number,
  ) => Promise<{
    resourceUrl: string
    fileName?: string
    sourceUrl?: string
    md5?: string
    size?: number
  }>
}): Promise<SkinApplyResult> {
  if (!params.entries.length) {
    reportProgress(params.onProgress, 100, '皮肤加载完成')
    return { roots: clearSkinResourcesFromRoots(params.roots) }
  }

  const nextRoots = clearSkinResourcesFromRoots(params.roots)
  const nameMap = new Map<string, string>()
  const total = params.entries.length

  for (let index = 0; index < total; index += 1) {
    const entry = params.entries[index]!
    const resolved = await params.resolveResource(entry, index, total)
    const mapped = appendMaterialResourceMappingEntry({
      root: nextRoots[entry.category] ?? null,
      category: entry.category,
      folderSegments: entry.folderSegments,
      fileName: resolved.fileName || entry.fileName,
      url: resolved.resourceUrl,
      sourceUrl: resolved.sourceUrl || entry.sourceUrl || '',
      md5: resolved.md5,
      size: resolved.size,
      extraLinks: {
        [SKIN_RESOURCE_LINK_INDEX]: SKIN_RESOURCE_MARKER,
      },
      replaceSameName: true,
    })
    nextRoots[entry.category] = mapped.root
    const relativeSegments = [...entry.folderSegments.slice(1), mapped.fileName]
    registerPathAliases(nameMap, entry.ouiPath, relativeSegments.join('/'))
  }

  if (params.includeReferencedPlaceholders === true) {
    const referencedFiles = collectReferencedSkinFilesFromProject(params.project)

    for (const ref of referencedFiles.values()) {
      if (replaceMappedPath(ref.ouiPath, nameMap) !== ref.ouiPath) continue
      const mapped = appendMaterialResourceMappingEntry({
        root: nextRoots[ref.category] ?? null,
        category: ref.category,
        folderSegments: ref.folderSegments,
        fileName: ref.fileName,
        url: '',
        sourceUrl: `oui-ref://${encodeURIComponent(ref.ouiPath)}`,
        extraLinks: {
          [SKIN_RESOURCE_LINK_INDEX]: SKIN_RESOURCE_MARKER,
        },
        replaceSameName: true,
      })
      nextRoots[ref.category] = mapped.root
      const relativeSegments = [...ref.folderSegments.slice(1), mapped.fileName]
      registerPathAliases(nameMap, ref.ouiPath, relativeSegments.join('/'))
    }
  }

  if (params.rewriteProjectPaths !== false) {
    rewriteProjectSkinPaths(params.project.systemData, nameMap)
    if (params.project.GameMallNew) rewriteProjectSkinPaths(params.project.GameMallNew, nameMap)
    if (params.project.GameMallLimite) rewriteProjectSkinPaths(params.project.GameMallLimite, nameMap)
    if (params.project.Welfare) rewriteProjectSkinPaths(params.project.Welfare, nameMap)
  }

  reportProgress(params.onProgress, 100, '皮肤加载完成')
  return {
    roots: nextRoots,
  }
}
